/**
 * Integrytas Group - Base App (MOBILE)
 * Page: LIC.STATUS — mobile-native (read-only).
 *
 * Touch-first counterpart of src/pages/admin/LicStatusPage.tsx. A DISTINCT
 * presentation (KPI tiles + tenant/entitlement summary + a searchable card list of
 * licensable modules with entitlement pills + a per-module detail bottom-sheet) over
 * the SAME data/logic layer only:
 *   - reads the verified entitlement via apiDb.licStatus() (the Ed25519-signed token
 *     the gateway verified — not a DB flag),
 *   - resolves each module's full display name from the prefix registry
 *     (INTEGRYTAS.MODULE.PREFIX_REGISTRY) so no bare catalog code reaches render as
 *     the primary label (§17.B4).
 * It is READ-ONLY — entitlements are issued by the central Licence Authority and
 * cannot be changed from this database, so there is no write path (no routed_writes).
 * It never re-renders the desktop grid; §16.
 */

import { useEffect, useMemo, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { Search, Inbox, X, KeyRound, BadgeCheck, Lock } from 'lucide-react';
import { apiDb } from '../../lib/api_client';
import { useFormatDateTime } from '../../lib/format_date';
import { MobilePageHeader, MobileStatStrip, MobileContentCard, MobileCardRow, MobileDetailRow, MobileFilterChips, MobileStatusPill } from '../components';
import type { MobilePageComponentProps } from '../mobile_page_components';

interface LicStatus {
  licensing_enabled: boolean;
  tenant: { app: string; customer: string } | null;
  available: boolean;
  modules: string[];
  catalog: string[];
  expires_at: string | null;
}

interface LicModuleRow {
  code: string;
  name: string;
  licensed: boolean;
}

type StatusFacet = 'all' | 'licensed' | 'unlicensed';

// Last-resort names if the prefix registry is missing an entry (mirrors the desktop
// page so a mobile viewer never sees a bare code where a name is expected).
const FALLBACK_NAMES: Record<string, string> = {
  csm: 'Common Security Module',
  cms: 'Content Management System',
  eds: 'Email Distribution System',
  dms: 'Document Management System',
  gsi: 'Global Search Indexer',
  fdm: 'Framework Dependency Map',
  cbm: 'Circuit Breaker Manager',
  ike: 'Idempotency Key Engine',
  dse: 'Dynamic Shadow-Field Engine',
  udc: 'User-Defined Column Engine',
  phe: 'Pipeline Hook Engine',
  uld: 'UI Layout Delta Engine',
  dad: 'Dynamic API Docs & Sandbox',
  ror: 'Read-Optimization Router',
};

function resolveName(code: string, names: Record<string, string>): string {
  return names[code] ?? names[code.toLowerCase()] ?? FALLBACK_NAMES[code.toLowerCase()] ?? code;
}

export function MobileLicStatusPage(_props: MobilePageComponentProps) {
  const { t } = useTranslation();
  const fmtDateTime = useFormatDateTime();
  const [status, setStatus] = useState<LicStatus | null>(null);
  const [names, setNames] = useState<Record<string, string>>({});
  const [filter, setFilter] = useState('');
  const [statusFacet, setStatusFacet] = useState<StatusFacet>('all');
  const [selected, setSelected] = useState<string | null>(null);
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const reload = async () => {
    setBusy(true);
    setError(null);
    try {
      // The verified entitlement and the prefix registry (prefix -> full name) are
      // independent reads — fetch them together.
      const [licRes, regRes] = await Promise.all([
        apiDb.licStatus<LicStatus>(),
        apiDb
          .from('csm_system_parameters')
          .select('parameter_value')
          .eq('internal_code', 'INTEGRYTAS.MODULE.PREFIX_REGISTRY'),
      ]);
      if (licRes.error) throw licRes.error;
      setStatus(licRes.data ?? null);

      const raw = (regRes.data as { parameter_value: string }[] | null)?.[0]?.parameter_value;
      if (raw) {
        try {
          setNames(JSON.parse(raw) as Record<string, string>);
        } catch {
          /* malformed registry — fall back to FALLBACK_NAMES / the code */
        }
      }
    } catch (e) {
      setError((e as Error).message);
    } finally {
      setBusy(false);
    }
  };

  // Load once on mount — the entitlement is not locale-dependent (module names come
  // from the registry JSON, not a _tl table), so no locale dependency here.
  useEffect(() => { void reload(); }, []);

  const rows = useMemo<LicModuleRow[]>(() => {
    const catalog = status?.catalog ?? [];
    const enabled = new Set(status?.modules ?? []);
    return catalog.map((code) => ({
      code,
      name: resolveName(code, names),
      licensed: enabled.has(code),
    }));
  }, [status, names]);

  const licensedCount = useMemo(() => rows.filter((r) => r.licensed).length, [rows]);

  const filtered = useMemo(() => {
    const q = filter.trim().toLowerCase();
    return rows.filter((r) => {
      if (statusFacet === 'licensed' && !r.licensed) return false;
      if (statusFacet === 'unlicensed' && r.licensed) return false;
      if (!q) return true;
      return r.name.toLowerCase().includes(q) || r.code.toLowerCase().includes(q);
    });
  }, [rows, filter, statusFacet]);

  const facets: { key: StatusFacet; label: string }[] = [
    { key: 'all', label: t('mobile.lic.all', 'All') },
    { key: 'licensed', label: t('mobile.lic.licensed', 'Licensed') },
    { key: 'unlicensed', label: t('mobile.lic.unlicensed', 'Not licensed') },
  ];

  const selectedRow = selected ? rows.find((r) => r.code === selected) ?? null : null;
  const tenantText = status?.tenant ? `${status.tenant.app}.${status.tenant.customer}` : '—';
  const expiresText = status?.expires_at ? fmtDateTime(status.expires_at) : '—';

  return (
    <section className="m-screen">
      <MobilePageHeader
        title={t('mobile.lic.title', 'Licensing')}
        description={t('mobile.lic.description', 'Which catalog modules this tenant is entitled to run.')}
      />

      {/* Entitlement stat strip */}
      <MobileStatStrip
        stats={[
          { value: licensedCount, label: t('mobile.lic.kpi_licensed', 'Licensed') },
          { value: rows.length, label: t('mobile.lic.kpi_catalog', 'In catalog') },
          { value: rows.length - licensedCount, label: t('mobile.lic.kpi_unlicensed', 'Not licensed') },
        ]}
      />

      {/* Tenant + authority summary */}
      {status && (
        <MobileContentCard>
          <MobileDetailRow
            label={t('mobile.lic.licensing', 'Licensing')}
            value={
              <MobileStatusPill tone={status.licensing_enabled ? 'info' : 'wait'}>
                {status.licensing_enabled
                  ? t('mobile.lic.enforced', 'Enforced')
                  : t('mobile.lic.not_configured', 'Not configured')}
              </MobileStatusPill>
            }
          />
          <MobileDetailRow label={t('mobile.lic.tenant', 'Tenant')} value={tenantText} />
          <MobileDetailRow
            label={t('mobile.lic.authority', 'Authority')}
            value={
              <MobileStatusPill tone={status.available ? 'ok' : 'no'}>
                {status.available
                  ? t('mobile.lic.verified', 'Verified')
                  : t('mobile.lic.unavailable', 'Unavailable')}
              </MobileStatusPill>
            }
          />
          <MobileDetailRow label={t('mobile.lic.expires', 'Expires')} value={expiresText} />
        </MobileContentCard>
      )}

      <div className="m-searchbox">
        <Search />
        <input
          value={filter}
          onChange={(e) => setFilter(e.target.value)}
          placeholder={t('mobile.lic.search', 'Search modules')}
          aria-label={t('mobile.lic.search', 'Search modules')}
        />
      </div>

      <MobileFilterChips
        chips={facets.map((f) => ({ value: f.key, label: f.label }))}
        isActive={(v) => statusFacet === v}
        onSelect={(v) => setStatusFacet(v as StatusFacet)}
      />

      {error && (
        <MobileContentCard>
          <div className="m-empty" style={{ color: 'var(--color-danger)' }}>{error}</div>
        </MobileContentCard>
      )}

      {filtered.length === 0 && !busy ? (
        <MobileContentCard><div className="m-empty"><Inbox /> {t('mobile.lic.none', 'No modules match')}</div></MobileContentCard>
      ) : (
        <MobileContentCard list>
          {filtered.map((r) => (
            <MobileCardRow
              key={r.code}
              lead={r.licensed ? <BadgeCheck /> : <Lock />}
              leadTone={r.licensed ? 'green' : 'slate'}
              title={r.name}
              subtitle={r.code}
              trailing={
                <MobileStatusPill tone={r.licensed ? 'ok' : 'no'}>
                  {r.licensed ? t('mobile.lic.licensed', 'Licensed') : t('mobile.lic.unlicensed', 'Not licensed')}
                </MobileStatusPill>
              }
              chevron
              onClick={() => setSelected(r.code)}
            />
          ))}
        </MobileContentCard>
      )}

      {/* Detail bottom-sheet (read-only) */}
      <div className={'m-scrim' + (selectedRow ? ' m-open' : '')} onClick={() => setSelected(null)} />
      <div className={'m-sheet' + (selectedRow ? ' m-open' : '')} aria-label={t('mobile.lic.detail', 'Module entitlement')}>
        <div className="m-grabber" />
        {selectedRow && (
          <>
            <div className="m-sheet-id">
              <span className="m-av"><KeyRound size={20} /></span>
              <span>
                <div className="m-nm">{selectedRow.name}</div>
                <div className="m-em">{selectedRow.code}</div>
              </span>
            </div>
            <MobileContentCard>
              <MobileDetailRow
                label={t('mobile.lic.entitlement', 'Entitlement')}
                value={
                  <MobileStatusPill tone={selectedRow.licensed ? 'ok' : 'no'}>
                    {selectedRow.licensed
                      ? t('mobile.lic.licensed', 'Licensed')
                      : t('mobile.lic.unlicensed', 'Not licensed')}
                  </MobileStatusPill>
                }
              />
              <MobileDetailRow label={t('mobile.lic.tenant', 'Tenant')} value={tenantText} />
              <MobileDetailRow label={t('mobile.lic.expires', 'Expires')} value={expiresText} />
            </MobileContentCard>
            <MobileContentCard>
              <p className="m-s">
                {t('mobile.lic.note', 'Entitlements are issued by the central Licence Authority and cryptographically verified — they cannot be changed from this database.')}
              </p>
            </MobileContentCard>
            <div className="m-actionbar">
              <button type="button" className="m-btn m-btn-ghost" onClick={() => setSelected(null)} aria-label={t('mobile.close', 'Close')}>
                <X size={16} />
              </button>
            </div>
          </>
        )}
      </div>
    </section>
  );
}
